/**
 * Module: screens/creator/store
 * Role: Zustand wizard draft state for creator onboarding (FTR-1106).
 *       Persisted to localStorage with 24h TTL so browser refresh doesn't lose
 *       partially-filled steps. On success or explicit clear the store is reset.
 *
 * Uses: zustand, zustand/middleware (persist)
 * Used by:
 *   - screens/creator/CreatorOnboardingScreen.tsx (root)
 *   - screens/creator/steps/*.tsx (each wizard step)
 *   - screens/creator/StatusScreen.tsx (reads rate-limit error)
 *
 * Glossary: ai/glossary/buyer.md
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface WizardDraft {
  /** Step 1 */
  vk_url: string;
  tiktok_url: string;
  /** Step 2 — base64 data URL (selfie) */
  selfie_dataUrl: string;
  /** Step 3 — combined consent (152-ФЗ + оферта). FTR-1106 amendment 2026-06-07:
   *  UI shows 2 checkboxes (design Eqq6AbBPpm1zPPyErlkgJQ chat16), backend keeps
   *  one `consent: boolean` field — both checkboxes must be ticked → consent=true. */
  consent: boolean;
  /** Steps 4-7 — profile fields. FTR-1106 amendment 2026-06-07: profile flattened
   *  from one sub-stepped screen into 4 separate steps (categories/formats/
   *  demographics/cities), screenshot step removed (auto-checked by API). */
  categories: string[];
  formats: string[];
  gender: string;
  age_range: string;
  cities: string[];
  /** Rate-limit error from last submit attempt (seconds until retry) */
  rateLimitRetryAt: number | null; // unix timestamp (ms)
  /** Timestamp for TTL check (ms since epoch) */
  createdAt: number;
}

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY = 'creator-wizard-draft-v1';

interface WizardStore {
  draft: WizardDraft;
  setUrl(vk_url: string, tiktok_url: string): void;
  setSelfie(dataUrl: string): void;
  setConsent(value: boolean): void;
  setProfile(data: Partial<Pick<WizardDraft, 'categories' | 'formats' | 'gender' | 'age_range' | 'cities'>>): void;
  setRateLimit(retryAt: number | null): void;
  resetDraft(): void;
}

function emptyDraft(): WizardDraft {
  return {
    vk_url: '',
    tiktok_url: '',
    selfie_dataUrl: '',
    consent: false,
    categories: [],
    formats: [],
    gender: '',
    age_range: '',
    cities: [],
    rateLimitRetryAt: null,
    createdAt: Date.now(),
  };
}

export const useWizardStore = create<WizardStore>()(
  persist(
    (set) => ({
      draft: emptyDraft(),
      setUrl(vk_url, tiktok_url) {
        set((s) => ({ draft: { ...s.draft, vk_url, tiktok_url } }));
      },
      setSelfie(dataUrl) {
        set((s) => ({ draft: { ...s.draft, selfie_dataUrl: dataUrl } }));
      },
      setConsent(value) {
        set((s) => ({ draft: { ...s.draft, consent: value } }));
      },
      setProfile(data) {
        set((s) => ({ draft: { ...s.draft, ...data } }));
      },
      setRateLimit(retryAt) {
        set((s) => ({ draft: { ...s.draft, rateLimitRetryAt: retryAt } }));
      },
      resetDraft() {
        set({ draft: emptyDraft() });
      },
    }),
    {
      name: STORAGE_KEY,
      // Expire draft after 24h. Also strips legacy `screenshot_dataUrl`
      // left behind by pre-2026-06-07 drafts (FTR-1106 amendment removed the
      // screenshot step — old persisted blob would silently survive otherwise).
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const age = Date.now() - (state.draft.createdAt ?? 0);
        if (age > DRAFT_TTL_MS) {
          state.resetDraft();
          return;
        }
        const draft = state.draft as WizardDraft & { screenshot_dataUrl?: string };
        if ('screenshot_dataUrl' in draft) {
          delete draft.screenshot_dataUrl;
        }
      },
    },
  ),
);
